import { useState } from "react";
import {
  ApiError,
  api,
  type CasoContacto,
  type CasoDetalle,
  type Contacto,
  type RolCasoContacto,
} from "@/lib/api";
import { canEdit, useAuth } from "@/lib/auth";
import AgregarContactoCasoModal from "@/components/AgregarContactoCasoModal";
import EditarContactoModal from "@/components/EditarContactoModal";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { useToast } from "@/ui/Toast";

type Props = {
  caso: CasoDetalle;
  onChanged: () => void;
};

/** Panel de "Contactos del caso" — muestra a quienes nos contactaron por
 * este caso (principal + seguimientos). El principal recibe el correo
 * directo (To); los seguimientos van en CC por default al enviar correo
 * cliente.
 *
 * Acciones:
 *   - Cambiar rol (principal <-> seguimiento). Cambiar a principal
 *     demarca al anterior automaticamente (constraint en DB).
 *   - Quitar del caso (no borra al contacto, solo lo desvincula).
 *   - Agregar otro contacto del cliente al caso.
 */
export default function CasoContactosPanel({ caso, onChanged }: Props) {
  const me = useAuth((s) => s.user);
  const toast = useToast();
  // Caso cerrado = sin cambios. Las acciones (agregar, cambiar rol,
  // quitar) se ocultan; los chips siguen visibles para auditoria.
  const editable = canEdit(me) && caso.estado !== "Cerrado";

  const [agregarOpen, setAgregarOpen] = useState(false);
  const [editando, setEditando] = useState<Contacto | null>(null);

  async function abrirEdicion(cc: CasoContacto) {
    // El modal de edición necesita el Contacto COMPLETO de la ficha
    // (incluye `principal`, que el chip del caso no trae).
    try {
      const lista = await api.get<Contacto[]>(
        `/api/clientes/${caso.cliente_id}/contactos?incluir_inactivos=true`,
      );
      const c = lista.find((x) => x.id === cc.contacto_id);
      if (!c) {
        toast.error("No encontré el contacto en la ficha del cliente");
        return;
      }
      setEditando(c);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar el contacto", msg);
    }
  }
  const [confirmQuitar, setConfirmQuitar] = useState<CasoContacto | null>(null);
  const [cambiandoRolId, setCambiandoRolId] = useState<number | null>(null);

  async function cambiarRol(cc: CasoContacto, nuevoRol: RolCasoContacto) {
    if (cc.rol === nuevoRol) return;
    setCambiandoRolId(cc.contacto_id);
    try {
      await api.patch<CasoContacto>(
        `/api/casos/${caso.id}/contactos/${cc.contacto_id}`,
        { rol: nuevoRol },
      );
      toast.success(
        nuevoRol === "principal"
          ? `${cc.nombre} ahora es el principal`
          : `${cc.nombre} pasa a seguimiento`,
      );
      onChanged();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cambiar el rol", msg);
    } finally {
      setCambiandoRolId(null);
    }
  }

  async function quitar(cc: CasoContacto) {
    try {
      await api.del(`/api/casos/${caso.id}/contactos/${cc.contacto_id}`);
      toast.success("Contacto quitado del caso", cc.nombre);
      setConfirmQuitar(null);
      onChanged();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude quitar el contacto", msg);
    }
  }

  const tienePrincipal = caso.contactos.some((c) => c.rol === "principal");

  return (
    <div className="space-y-1.5">
      {!tienePrincipal && caso.contactos.length > 0 && (
        <p className="text-xs text-amber-300">
          Falta un contacto <strong>principal</strong> — sin él no se puede
          enviar correo al cliente.
        </p>
      )}

      {caso.contactos.length === 0 ? (
        <p className="text-xs text-zinc-500">
          Sin contactos asignados.{" "}
          {editable && (
            <button
              onClick={() => setAgregarOpen(true)}
              className="text-accent-400 hover:text-accent-300 transition"
            >
              + agregar contacto
            </button>
          )}
        </p>
      ) : (
        <>
          {/* Una fila por contacto: nombre + chip de rol + acciones (★/✕)
              PEGADAS al contacto para que se vea a cuál aplican; email y
              teléfono bajan a una segunda línea. */}
          <ul className="space-y-1">
            {caso.contactos.map((cc) => {
              const detalles = [cc.email, cc.telefono]
                .filter(Boolean)
                .join(" · ");
              // Quién lo agregó, a la vista (antes vivía en un tooltip del nombre).
              const agregado = cc.added_by_nombre
                ? `agregado por ${cc.added_by_nombre} el ${new Date(cc.added_at).toLocaleDateString()}`
                : null;
              return (
                <li key={cc.id} className="py-0.5">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    <span className="text-zinc-200 break-words">{cc.nombre}</span>
                    {cc.rol === "principal" ? (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-accent-500/30 bg-accent-500/10 text-accent-300">
                        principal
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-zinc-600 bg-zinc-700/30 text-zinc-400">
                        seguimiento
                      </span>
                    )}
                    {!cc.activo && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-zinc-600 bg-zinc-700/30 text-zinc-400">
                        archivado
                      </span>
                    )}
                    {editable && (
                      <span className="inline-flex items-center gap-0.5">
                        {cc.rol === "seguimiento" ? (
                          <button
                            onClick={() => void cambiarRol(cc, "principal")}
                            disabled={cambiandoRolId === cc.contacto_id}
                            aria-label={`Hacer principal a ${cc.nombre}`}
                            className="px-1.5 py-0.5 rounded text-zinc-500 hover:text-amber-300 hover:bg-ink-800 disabled:opacity-50 transition"
                          >
                            ☆
                          </button>
                        ) : (
                          <button
                            onClick={() => void cambiarRol(cc, "seguimiento")}
                            disabled={cambiandoRolId === cc.contacto_id}
                            aria-label={`Pasar a seguimiento a ${cc.nombre}`}
                            className="px-1.5 py-0.5 rounded text-amber-300 hover:text-zinc-300 hover:bg-ink-800 disabled:opacity-50 transition"
                          >
                            ★
                          </button>
                        )}
                        <button
                          onClick={() => void abrirEdicion(cc)}
                          aria-label={`Editar a ${cc.nombre}`}
                          className="px-1.5 py-0.5 rounded text-zinc-500 hover:text-accent-300 hover:bg-ink-800 transition"
                        >
                          ✎
                        </button>
                        <button
                          onClick={() => setConfirmQuitar(cc)}
                          aria-label={`Quitar a ${cc.nombre} del caso`}
                          className="px-1.5 py-0.5 rounded text-red-400/80 hover:text-red-300 hover:bg-ink-800 transition"
                        >
                          ✕
                        </button>
                      </span>
                    )}
                  </div>
                  {detalles && (
                    <div className="text-xs text-zinc-500 break-all">{detalles}</div>
                  )}
                  {agregado && <div className="text-[11px] text-zinc-600">{agregado}</div>}
                </li>
              );
            })}
          </ul>
          {editable && (
            <button
              onClick={() => setAgregarOpen(true)}
              className="text-xs text-accent-400 hover:text-accent-300 transition"
            >
              + agregar contacto
            </button>
          )}
        </>
      )}

      {agregarOpen && (
        <AgregarContactoCasoModal
          open={true}
          onClose={() => setAgregarOpen(false)}
          caso={caso}
          onAdded={() => {
            setAgregarOpen(false);
            onChanged();
          }}
        />
      )}

      {editando && (
        <EditarContactoModal
          open={true}
          onClose={() => setEditando(null)}
          clienteId={caso.cliente_id}
          clienteNombre={caso.cliente_nombre}
          contacto={editando}
          onSaved={() => {
            setEditando(null);
            onChanged();
          }}
        />
      )}

      {confirmQuitar && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmQuitar(null)}
          title={`Quitar a "${confirmQuitar.nombre}" del caso`}
          description={
            confirmQuitar.rol === "principal"
              ? "Quitarás al contacto principal. El caso quedará sin principal hasta que asignes otro; no podrás enviar correo cliente mientras tanto. Esto no borra al contacto de la ficha del cliente."
              : "Solo lo desvincula del caso. El contacto sigue existiendo en la ficha del cliente."
          }
          confirmLabel="Quitar"
          onConfirm={() => quitar(confirmQuitar)}
        />
      )}
    </div>
  );
}
